import BotoesSalvarExcluir from '@/shared/components/forms/BotoesSalvarExcluir';
import Links from '@/shared/constants/navegacao';
import ModalExcluir from '@/shared/components/modals/ModalExcluir';
import { EditadoPorRodape } from '@/shared/components/EditadoPorRodape';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ProcessoSeletivoService, CreateVagaDto, CreateVagaApiDto, ReadCursoDto } from './processo-seletivo.service';
import type { CreateProcessoSeletivoDto } from '@/api.types';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Endpoints } from '@/shared/constants/endpoints';
import { API_URL } from '@/shared/constants/strings';
import axios from 'axios';

/** Valores do formulário: valorTaxa é string no UI (máscara); vagas são CreateVagaDto no UI (convertidas no submit) */
type ProcessoSeletivoFormValues = Omit<CreateProcessoSeletivoDto, 'valorTaxa' | 'vagas'> & {
  valorTaxa?: number | string;
  vagas?: CreateVagaDto[];
  // Campos vindos da leitura (não fazem parte do DTO de criação)
  editadoPor?: { username?: string };
  editadoEm?: Date | string;
};
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import { CursoService } from '@/modules/structure/curso/curso.service';
import { MunicipioService, MunicipioIBGE } from '@/modules/structure/municipio/municipio.service';
import { Modal, Button } from 'react-bootstrap';
import { aplicarMascaraMoeda, removerMascaraMoeda, formatarMoeda } from '@/shared/utils/numeros';

const processoSeletivoService = new ProcessoSeletivoService();
const cursoService = new CursoService();
const municipioService = new MunicipioService();

export default function ProcessoSeletivoEditPage() {
  const { register, handleSubmit, reset, getValues, watch, setValue } = useForm<ProcessoSeletivoFormValues>();

  const navigate = useNavigate();
  const params = useParams();
  const [modaExcluirShow, setModalExcluirShow] = useState(false);
  const [modalMunicipiosShow, setModalMunicipiosShow] = useState(false);
  const [vagaSelecionadaIndex, setVagaSelecionadaIndex] = useState<number | null>(null);
  const [municipiosSelecionadosModal, setMunicipiosSelecionadosModal] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [cursos, setCursos] = useState<ReadCursoDto[]>([]);
  const [municipios, setMunicipios] = useState<MunicipioIBGE[]>([]);
  const [vagas, setVagas] = useState<CreateVagaDto[]>([]);
  const [provaArquivo, setProvaArquivo] = useState<File | null>(null);
  const [provaUrlAtual, setProvaUrlAtual] = useState<string | null>(null);

  const MAX_SIZE_ARQUIVO_BYTES_PROVA = 3 * 1024 * 1024; // 3MB

  const id = params.id ? Number(params.id) : 0;
  const btnSalvar = id > 0 ? 'Salvar' : 'Cadastrar';
  const temTaxaInscricao = watch('temTaxaInscricao');
  const comProva = watch('comProva');

  useEffect(() => {
    // Carregar cursos
    cursoService.findAll({ skip: 0, take: 9999 })
      .then((result) => {
        setCursos(result[0]);
      })
      .catch((error) => {
        toast.error('Falha ao carregar cursos: ' + error);
      });

    // Carregar municípios de Sergipe do IBGE
    municipioService.getMunicipiosSergipe()
      .then((result) => {
        setMunicipios(result);
      })
      .catch((error) => {
        toast.error('Falha ao carregar municípios: ' + error);
      });

    // Carregar registro se estiver editando
    if (id > 0) {
      processoSeletivoService.findOne(id)
        .then((result) => {
          if (result) {
            // Formatar datas para datetime-local (YYYY-MM-DDTHH:mm)
            const formatarParaDateTimeLocal = (date: Date | string) => {
              if (!date) return '';
              const d = new Date(date);
              const year = d.getFullYear();
              const month = String(d.getMonth() + 1).padStart(2, '0');
              const day = String(d.getDate()).padStart(2, '0');
              const hours = String(d.getHours()).padStart(2, '0');
              const minutes = String(d.getMinutes()).padStart(2, '0');
              return `${year}-${month}-${day}T${hours}:${minutes}`;
            };

            // Formatar datas para input date (YYYY-MM-DD), sem deslocar por fuso
            const formatarParaDateInput = (date: Date | string) => {
              if (!date) return '';
              if (typeof date === 'string') {
                const match = date.match(/^(\d{4}-\d{2}-\d{2})/);
                if (match) return match[1];
              }
              const d = new Date(date);
              const year = d.getUTCFullYear();
              const month = String(d.getUTCMonth() + 1).padStart(2, '0');
              const day = String(d.getUTCDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            };

            // Agrupar vagas por curso, mantendo municípios únicos
            const vagasAgrupadas = new Map<number, CreateVagaDto>();
            result.vagas?.forEach(v => {
              if (!vagasAgrupadas.has(v.cursoId)) {
                vagasAgrupadas.set(v.cursoId, {
                  cursoId: v.cursoId,
                  municipios: [],
                });
              }
              const vagaAgrupada = vagasAgrupadas.get(v.cursoId)!;
              // Adicionar município apenas se não existir
              if (v.municipio && !vagaAgrupada.municipios.includes(v.municipio)) {
                vagaAgrupada.municipios.push(v.municipio);
              }
            });

            const cursoIds = result.cursos?.map(c => c.id) || [];
            const registroFormatado: ProcessoSeletivoFormValues = {
              ...result,
              dataInicioInscricoes: result.dataInicioInscricoes ? formatarParaDateTimeLocal(result.dataInicioInscricoes) : '',
              dataFimInscricoes: result.dataFimInscricoes ? formatarParaDateTimeLocal(result.dataFimInscricoes) : '',
              dataVencimentoBoleto: result.dataVencimentoBoleto ? formatarParaDateInput(result.dataVencimentoBoleto) : '',
              valorTaxa: result.valorTaxa != null ? formatarMoeda(result.valorTaxa) : '',
              cursoIds: cursoIds,
              vagas: Array.from(vagasAgrupadas.values()),
            };
            reset(registroFormatado);
            setVagas(Array.from(vagasAgrupadas.values()));
            setProvaUrlAtual(result.provaUrl ?? null);
          }
        })
        .catch((res) => {
          toast.error(String(res));
        });
    }
  }, [id, reset]);


  const onSubmit: SubmitHandler<ProcessoSeletivoFormValues> = async (data) => {
    // Validar vagas duplicadas antes de enviar
    if (!validarVagasDuplicadas()) {
      return;
    }

    // Validar datas antes de enviar
    if (data.dataInicioInscricoes && data.dataFimInscricoes) {
      const dataInicio = new Date(data.dataInicioInscricoes);
      const dataFim = new Date(data.dataFimInscricoes);
      if (dataFim <= dataInicio) {
        toast.error('A data final das inscrições deve ser posterior à data inicial.');
        return;
      }
    }

    setIsLoading(true);

    // Converter vagas para o formato do backend (uma vaga por curso+município)
    const vagasParaEnvio: CreateVagaApiDto[] = [];
    const vagasMap = new Map<string, boolean>(); // Para evitar duplicatas

    vagas.forEach(vaga => {
      if (vaga.cursoId > 0) {
        if (vaga.municipios && vaga.municipios.length > 0) {
          // Criar uma vaga para cada município selecionado
          vaga.municipios.forEach(municipio => {
            const key = `${vaga.cursoId}-${municipio}`;
            if (!vagasMap.has(key)) {
              vagasMap.set(key, true);
              vagasParaEnvio.push({
                cursoId: vaga.cursoId,
                municipio,
                quantidade: 1
              });
            }
          });
        } else {
          // Se não há municípios, criar vaga apenas com curso
          const key = `${vaga.cursoId}-null`;
          if (!vagasMap.has(key)) {
            vagasMap.set(key, true);
            vagasParaEnvio.push({
              cursoId: vaga.cursoId,
              quantidade: 1
            });
          }
        }
      }
    });

    const dadosEnvio: CreateProcessoSeletivoDto = {
      nome: data.nome,
      dataInicioInscricoes: data.dataInicioInscricoes,
      dataFimInscricoes: data.dataFimInscricoes,
      status: data.status,
      temTaxaInscricao: data.temTaxaInscricao,
      comProva: data.comProva ?? false,
      valorTaxa: typeof data.valorTaxa === 'string' ? removerMascaraMoeda(data.valorTaxa) : (data.valorTaxa || 0),
      dataVencimentoBoleto: data.dataVencimentoBoleto,
      editalUrl: data.editalUrl,
      cursoIds: vagas.filter(v => v.cursoId > 0).map(v => v.cursoId),
      vagas: vagasParaEnvio.length > 0 ? vagasParaEnvio : undefined,
    };

    const exigeProva = data.comProva === true;

    if (exigeProva && !provaArquivo && !provaUrlAtual) {
      toast.error('Por favor, anexe a prova do processo seletivo (PDF).');
      setIsLoading(false);
      return;
    }

    try {
      if (id > 0) {
        await processoSeletivoService.update(id, dadosEnvio);

        if (exigeProva && provaArquivo) {
          await processoSeletivoService.uploadProva(id, provaArquivo);
        }

        toast.success("Processo seletivo atualizado.");
        navigate(`${Links.ProcessoSeletivo_Index}`);
      } else {
        const res = await processoSeletivoService.create(dadosEnvio);

        if (exigeProva && provaArquivo) {
          await processoSeletivoService.uploadProva(res.id, provaArquivo);
        }

        toast.success("Processo seletivo cadastrado.");
        if (res?.emailsNotificacaoEnviados) {
          toast.info("E-mails de notificação foram enviados aos destinatários.");
        }
        navigate(`${Links.ProcessoSeletivo_Index}`);
      }
    } catch (res) {
      toast.error(String(res));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadProvaProcessoSeletivo = async () => {
    try {
      if (!id || !provaUrlAtual) return;

      const response = await axios.get(
        `${API_URL}${Endpoints.PROCESSO_SELETIVO}/prova/file/${id}`,
        { responseType: 'blob' },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'prova.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Erro ao baixar a prova do processo seletivo.');
    }
  };

  const handleExcluir = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setModalExcluirShow(false);
    processoSeletivoService.remove(id)
      .then(() => {
        toast.warning("Processo seletivo removido.");
        navigate(`${Links.ProcessoSeletivo_Index}`);
      })
      .catch((res) => {
        toast.error(String(res));
      });
  };

  const validarVagasDuplicadas = (): boolean => {
    const vagasValidas = vagas.filter(v => v.cursoId > 0);
    const cursosUsados = new Set<number>();

    // Verificar se há cursos duplicados
    for (let i = 0; i < vagasValidas.length; i++) {
      const vaga = vagasValidas[i];
      if (cursosUsados.has(vaga.cursoId)) {
        toast.error(`Não é permitido cadastrar vagas duplicadas. O curso já foi selecionado em outra vaga.`);
        return false;
      }
      cursosUsados.add(vaga.cursoId);
    }

    return true;
  };

  const adicionarVaga = () => {
    setVagas([...vagas, { cursoId: 0, municipios: [] }]);
  };

  const removerVaga = (index: number) => {
    setVagas(vagas.filter((_, i) => i !== index));
  };

  const atualizarCursoVaga = (index: number, cursoId: number) => {
    const novasVagas = [...vagas];
    novasVagas[index] = { ...novasVagas[index], cursoId };

    // Validar duplicatas de curso
    const cursoDuplicado = novasVagas.findIndex((v, i) =>
      i !== index &&
      v.cursoId === cursoId &&
      v.cursoId > 0
    );

    if (cursoDuplicado !== -1) {
      toast.warning(`Este curso já foi selecionado na vaga ${cursoDuplicado + 1}. Não é permitido cadastrar vagas duplicadas.`);
      return;
    }

    setVagas(novasVagas);
  };

  const abrirModalMunicipios = (index: number) => {
    setVagaSelecionadaIndex(index);
    const vagaAtual = vagas[index];
    setMunicipiosSelecionadosModal(vagaAtual?.municipios || []);
    setModalMunicipiosShow(true);
  };

  const fecharModalMunicipios = () => {
    setModalMunicipiosShow(false);
    setVagaSelecionadaIndex(null);
    setMunicipiosSelecionadosModal([]);
  };

  const toggleMunicipioModal = (municipioNome: string) => {
    if (municipiosSelecionadosModal.includes(municipioNome)) {
      setMunicipiosSelecionadosModal(municipiosSelecionadosModal.filter(nome => nome !== municipioNome));
    } else {
      setMunicipiosSelecionadosModal([...municipiosSelecionadosModal, municipioNome]);
    }
  };

  const salvarMunicipiosSelecionados = () => {
    if (vagaSelecionadaIndex === null) return;

    const novasVagas = [...vagas];
    const vagaAtual = novasVagas[vagaSelecionadaIndex];

    novasVagas[vagaSelecionadaIndex] = {
      ...vagaAtual,
      municipios: municipiosSelecionadosModal,
    };

    setVagas(novasVagas);
    fecharModalMunicipios();
  };

  return (
    <>
      <style>{`
        /* Estilos modernos para datetime-local */
        input[type="datetime-local"] {
          padding: 0.875rem 1.125rem 0.875rem 3rem !important;
          font-size: 1rem !important;
          border: 2px solid #e0e0e0 !important;
          border-radius: 10px !important;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
          background-color: #ffffff !important;
          background-image: linear-gradient(to right, #f8f9fa 0%, #ffffff 100%) !important;
          cursor: pointer !important;
          color: #212529 !important;
          font-weight: 500 !important;
          width: 100% !important;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
        }

        input[type="datetime-local"]:hover {
          border-color: #0d6efd !important;
          background-color: #f8f9ff !important;
          box-shadow: 0 4px 8px rgba(13, 110, 253, 0.1) !important;
          transform: translateY(-1px) !important;
        }

        input[type="datetime-local"]:focus {
          border-color: #0d6efd !important;
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.2), 0 4px 12px rgba(13, 110, 253, 0.15) !important;
          background-color: #ffffff !important;
          outline: none !important;
          transform: translateY(-1px) !important;
        }

        input[type="datetime-local"]:disabled {
          background-color: #f5f5f5 !important;
          cursor: not-allowed !important;
          opacity: 0.65 !important;
          border-color: #e0e0e0 !important;
        }

        /* Melhorar a aparência do calendário nativo */
        input[type="datetime-local"]::-webkit-calendar-picker-indicator {
          cursor: pointer;
          opacity: 1;
          filter: invert(0.4) sepia(1) saturate(3) hue-rotate(200deg);
          padding: 6px;
          margin-right: 8px;
          border-radius: 6px;
          background-color: rgba(13, 110, 253, 0.1);
          transition: all 0.2s ease;
        }

        input[type="datetime-local"]::-webkit-calendar-picker-indicator:hover {
          background-color: rgba(13, 110, 253, 0.2);
          filter: invert(0.2) sepia(1) saturate(5) hue-rotate(200deg);
          transform: scale(1.1);
        }

        input[type="datetime-local"]:focus::-webkit-calendar-picker-indicator {
          background-color: rgba(13, 110, 253, 0.25);
        }

        /* Para Firefox */
        input[type="datetime-local"]::-moz-calendar-picker-indicator {
          cursor: pointer;
          opacity: 1;
          padding: 6px;
          border-radius: 6px;
          background-color: rgba(13, 110, 253, 0.1);
          transition: all 0.2s ease;
        }

        input[type="datetime-local"]::-moz-calendar-picker-indicator:hover {
          background-color: rgba(13, 110, 253, 0.2);
        }

        /* Container do input com ícone */
        .datetime-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .datetime-input-wrapper::before {
          content: "📅";
          position: absolute;
          left: 14px;
          font-size: 1.2rem;
          pointer-events: none;
          z-index: 2;
          opacity: 0.7;
          transition: all 0.2s ease;
        }

        .datetime-input-wrapper:hover::before {
          opacity: 1;
          transform: scale(1.1);
        }

        .datetime-input-wrapper:focus-within::before {
          opacity: 1;
          transform: scale(1.15);
        }

        /* Label melhorado */
        .form-label.fw-bold {
          color: #495057;
          margin-bottom: 0.625rem;
          font-size: 0.95rem;
          font-weight: 600;
          letter-spacing: 0.01em;
        }

        /* Adicionar separador visual entre data e hora no input */
        input[type="datetime-local"]::placeholder {
          color: #adb5bd;
          font-weight: 400;
        }

        /* Melhorar legibilidade do texto */
        input[type="datetime-local"] {
          letter-spacing: 0.02em;
        }
      `}</style>
      <div className="row">
        <div className="col-md-8">
          <h3>Processo Seletivo</h3>
        </div>
        <div className="col-md-4">
          <br />
          <Link
            type="button"
            className="btn btn-primary float-end"
            to={Links.ProcessoSeletivo_Index}
          >
            Listagem
          </Link>
        </div>
      </div>
      <hr className="pb-4" />

      <form onSubmit={(event) => { void handleSubmit(onSubmit)(event); }}      >
        <div className="row">
          <div className="mb-3 col-md-7">
            <label htmlFor="nome" className="form-label">
              Nome do Processo Seletivo: *
            </label>
            <input
              id="nome"
              autoFocus={true}
              title="Nome"
              type="text"
              className="form-control"
              required
              {...register('nome')}
            />
          </div>

          <div className="mb-3 col-md-5">
            <div className="border-0 shadow-sm mb-4" role="status">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2" title="Endereço para divulgar no edital, site ou redes sociais.">
                <strong className="text-dark">Link público de inscrição</strong>
              </div>
              {id > 0 && (
                <div className="input-group input-group-sm flex-nowrap">
                  <input
                    type="text"
                    className="form-control font-monospace small"
                    readOnly
                    aria-label="URL pública de inscrição"
                    value={`${typeof window !== 'undefined' ? window.location.origin : ''}${Links.Candidato_Inscricao}/${id}`}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => {
                      const url = `${window.location.origin}${Links.Candidato_Inscricao}/${id}`;
                      void navigator.clipboard.writeText(url).then(() => {
                        toast.success('Link copiado para a área de transferência.');
                      });
                    }}
                  >
                    Copiar
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="mb-3 col-md-6">
            <label htmlFor="dataInicioInscricoes" className="form-label fw-bold">
              Data e Hora de Início das Inscrições: *
            </label>
            <div className="datetime-input-wrapper">
              <input
                id="dataInicioInscricoes"
                type="datetime-local"
                className="form-control"
                required
                {...register('dataInicioInscricoes', {
                  onChange: (e) => {
                    // Atualizar o min da data final quando a data inicial mudar
                    const dataFimInput = document.getElementById('dataFimInscricoes') as HTMLInputElement;
                    if (dataFimInput && e.target.value) {
                      dataFimInput.min = e.target.value;
                      // Se a data final for anterior à inicial, limpar
                      if (dataFimInput.value && dataFimInput.value < e.target.value) {
                        dataFimInput.value = '';
                      }
                    }
                  }
                })}
              />
            </div>
          </div>

          <div className="mb-3 col-md-6">
            <label htmlFor="dataFimInscricoes" className="form-label fw-bold">
              Data e Hora de Fim das Inscrições: *
            </label>
            <div className="datetime-input-wrapper">
              <input
                id="dataFimInscricoes"
                type="datetime-local"
                className="form-control"
                required
                min={watch('dataInicioInscricoes') || ''}
                {...register('dataFimInscricoes', {
                  validate: (value) => {
                    const dataInicio = watch('dataInicioInscricoes');
                    if (dataInicio && value && new Date(value) <= new Date(dataInicio)) {
                      return 'A data final deve ser posterior à data inicial';
                    }
                    return true;
                  }
                })}
              />
            </div>
          </div>

          <div className="mb-3 col-md-6">
            <label htmlFor="status" className="form-label">
              Status: *
            </label>
            <select
              id="status"
              className="form-select"
              required
              {...register('status')}
            >
              <option value="rascunho">Rascunho</option>
              <option value="pronto_para_iniciar">Pronto para iniciar</option>
              <option value="aberto">Aberto</option>
              <option value="fechado">Fechado</option>
              <option value="finalizado">Finalizado</option>
            </select>
          </div>

          <div className="mb-3 col-md-6">
            <label htmlFor="editalUrl" className="form-label">
              URL do Edital (PDF):
            </label>
            <input
              id="editalUrl"
              type="text"
              className="form-control"
              {...register('editalUrl')}
            />
          </div>

          <div className="mb-3 col-md-12 form-check">
            <input
              id="temTaxaInscricao"
              className="form-check-input"
              type="checkbox"
              {...register('temTaxaInscricao')}
            />
            <label className="form-check-label" htmlFor="temTaxaInscricao">
              Tem taxa de inscrição?
            </label>
          </div>

          <div className="mb-3 col-md-12">
            <div className="row">
              <div className="col-md-6">
                <div className="form-check">
                  <input
                    id="comProva"
                    className="form-check-input"
                    type="checkbox"
                    {...register('comProva')}
                  />
                  <label className="form-check-label" htmlFor="comProva">
                    Processo seletivo exige envio de prova?
                  </label>
                </div>
              </div>

              <div className="col-md-6">
                {comProva && (
                  <>
                    {provaUrlAtual && (
                      <div className="mb-2">
                        <small className="text-success d-block mb-1">
                          Já existe uma prova anexada no processo seletivo.
                        </small>
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={handleDownloadProvaProcessoSeletivo}
                        >
                          Baixar PDF anexado
                        </button>
                      </div>
                    )}
                    <label htmlFor="provaProcesso" className="form-label">
                      Anexar prova do processo seletivo (PDF, max 3MB)
                    </label>
                    <input
                      id="provaProcesso"
                      type="file"
                      className="form-control"
                      accept=".pdf,application/pdf"
                      onChange={(e) => {
                        const file = e.target.files?.[0] ?? null;
                        if (!file) {
                          setProvaArquivo(null);
                          return;
                        }

                        if (file.type !== 'application/pdf') {
                          toast.error('Apenas arquivos PDF são válidos para a prova.');
                          e.target.value = '';
                          setProvaArquivo(null);
                          return;
                        }

                        if (file.size > MAX_SIZE_ARQUIVO_BYTES_PROVA) {
                          toast.error('Só é permitido anexar prova com no máximo 3MB.');
                          e.target.value = '';
                          setProvaArquivo(null);
                          return;
                        }

                        setProvaArquivo(file);
                      }}
                    />
                  </>
                )}
              </div>
            </div>
          </div>

          {temTaxaInscricao && (
            <>
              <div className="mb-3 col-md-6">
                <label htmlFor="valorTaxa" className="form-label">
                  Valor da Taxa:
                </label>
                <input
                  id="valorTaxa"
                  type="text"
                  className="form-control"
                  placeholder="R$ 0,00"
                  {...register('valorTaxa', {
                    onChange: (e) => {
                      const valorFormatado = aplicarMascaraMoeda(e.target.value);
                      // Atualiza o valor no input
                      e.target.value = valorFormatado;
                      // Atualiza o valor no formulário (string com máscara)
                      setValue('valorTaxa', valorFormatado as ProcessoSeletivoFormValues['valorTaxa'], { shouldValidate: true, shouldDirty: true });
                    },
                    onBlur: (e) => {
                      // Garante que o valor está formatado ao sair do campo
                      if (e.target.value) {
                        const valorFormatado = aplicarMascaraMoeda(e.target.value);
                        e.target.value = valorFormatado;
                        setValue('valorTaxa', valorFormatado as ProcessoSeletivoFormValues['valorTaxa'], { shouldValidate: true });
                      }
                    }
                  })}
                />
              </div>

              <div className="mb-3 col-md-6">
                <label htmlFor="dataVencimentoBoleto" className="form-label">
                  Data de Vencimento do Boleto:
                </label>
                <input
                  id="dataVencimentoBoleto"
                  type="date"
                  className="form-control"
                  {...register('dataVencimentoBoleto', {
                    setValueAs: (value) =>
                      typeof value === 'string' ? value.split('T')[0] : value,
                  })}
                />
              </div>
            </>
          )}

          <div className="mb-3 col-md-12">
            {/* <label className="form-label">Vagas:</label> */}
            <button
              type="button"
              className="btn btn-sm btn-secondary mb-2"
              onClick={adicionarVaga}
            >
              {vagas.filter(v => v.cursoId > 0).length > 0
                ? `Adicionar Curso (${vagas.filter(v => v.cursoId > 0).length} selecionado(s))`
                : 'Adicionar Curso'}
            </button>
            {vagas.map((vaga, index) => {
              const municipiosSelecionadosCount = vaga.municipios?.length || 0;

              return (
                <div key={index} className="card mb-2">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-5">
                        <label className="form-label">Curso: *</label>
                        <select
                          className="form-select"
                          value={vaga.cursoId}
                          onChange={(e) => atualizarCursoVaga(index, Number(e.target.value))}
                        >
                          <option value={0}>Selecione um curso...</option>
                          {cursos.map((curso) => (
                            <option key={curso.id} value={curso.id}>
                              {curso.nome}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Municípios: *</label>
                        <div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary w-100"
                            onClick={() => abrirModalMunicipios(index)}
                          >
                            {municipiosSelecionadosCount > 0
                              ? `${municipiosSelecionadosCount} município(s) selecionado(s)`
                              : 'Selecionar Municípios'}
                          </button>
                        </div>
                      </div>
                      <div className="col-md-1">
                        <label className="form-label">&nbsp;</label>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger w-100"
                          onClick={() => removerVaga(index)}
                        >
                          X
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <EditadoPorRodape
            dados={{
              editadoPor: { username: getValues().editadoPor?.username },
              editadoEm: getValues().editadoEm,
            }}
          />

          <div>
            <BotoesSalvarExcluir
              isLoading={isLoading}
              texto={btnSalvar}
              textoLoading={btnSalvar === 'Cadastrar' ? ' Cadastrando...' : ' Salvando...'}
              id={id}
              onExcluirClick={() => setModalExcluirShow(true)}
            />
          </div>
        </div>
      </form>

      <ModalExcluir
        show={modaExcluirShow}
        handleExcluir={handleExcluir}
        handleHide={() => setModalExcluirShow(false)}
      ></ModalExcluir>

      <Modal
        show={modalMunicipiosShow}
        onHide={fecharModalMunicipios}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Selecionar Municípios</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <h5>Municípios de Sergipe</h5>
            {municipios.length === 0 ? (
              <p className="text-muted">Carregando municípios...</p>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {municipios.map((municipio) => {
                  const isChecked = municipiosSelecionadosModal.includes(municipio.nome);

                  return (
                    <div key={municipio.id} className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleMunicipioModal(municipio.nome)}
                        id={`municipio-${municipio.id}-${vagaSelecionadaIndex}`}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`municipio-${municipio.id}-${vagaSelecionadaIndex}`}
                      >
                        {municipio.nome}
                      </label>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={fecharModalMunicipios}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={salvarMunicipiosSelecionados}>
            Salvar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

